import ConstList from '@/ConstList'

/**
 * 勤務表の「今月消化（有給）」を、日ごとの勤怠区分から数える。
 *
 * ## なぜ切り出したか（2026-09-30 の仕様 B）
 *
 * 勤務表の承認は「その月の有休の消化を残日数から差し引く」形になった。
 * 差し引く日数は、サーバが承認した月の日次の勤怠区分から数える
 * （back の PaidLeaveUsage。休暇申請の突き合わせ selectLeaveGaps と同じ数え方）。
 *
 * 画面は前に、
 *
 * - 午前有給＋午後有給（7 と 14）を 0.5日
 * - 有給＋午後有給（6 と 14）を 1.5日
 *
 * と数えていた。画面とサーバで数え方がずれると、勤務表に出た月末残・今期消化と、
 * 承認したあとの残日数が食い違う。**サーバと同じ並びで数える。**
 *
 * ## 数え方
 *
 * 1日を午前の枠と午後の枠に分け、有給の枠を 0.5日ずつ数える。**1日を超えない。**
 *
 * - 午前の枠が有給：勤怠1が 有給（6）か 午前有給（7）
 * - 午後の枠が有給：勤怠1が 有給（6）（終日なので午後も有給）、または
 *   勤怠1が半日の区分（午前出勤・シフト・午前有給・午前代休・早出出勤・午前特休・午前欠勤）で
 *   勤怠2が 午後有給（14）
 *
 * 勤怠2を選べるのは、勤怠1が半日の区分のときだけ（Roster.vue の select）。
 * 常勤や休日の日に勤怠2の 14 が残っていても、午後は有給に数えない（サーバも数えない）。
 *
 * 特休（12・17・18）は有給に数えない（休暇申請でも別の種別）。
 * 代休の数え方はここでは扱わない（第1段では変えない）。
 */

/**
 * 午後の枠を勤怠2で決める勤怠1（半日の区分）。
 *
 * back の PaidLeaveUsage と selectLeaveGaps の `IN (3, 4, 7, 9, 16, 17, 11)` と同じ並び。
 * 勤怠1の 16 は早出出勤（勤怠2の 16 は午後欠勤。列が違う）。
 */
export const AFTERNOON_DECIDED_BY_SECOND = Object.freeze([
  ConstList.MORNING_WORK,
  ConstList.SHIFT,
  ConstList.MORNING_PAID,
  ConstList.MORNING_SUBSTITUTE,
  ConstList.EARLY_MORNING,
  ConstList.MORNING_SPECIAL_HOLIDAY,
  ConstList.MORNING_DEDUCTION
])

/** 画面の値は数値のはずだが、文字の "6" が来ても同じ区分として扱う。空は区分なし。 */
const toCode = value => {
  if (value === null || value === undefined || value === '') return null
  const code = Number(value)
  return Number.isFinite(code) ? code : null
}

/** 午前の枠が有給か。 */
export function morningIsPaid (attendance1) {
  const first = toCode(attendance1)
  return first === ConstList.PAID || first === ConstList.MORNING_PAID
}

/** 午後の枠が有給か。 */
export function afternoonIsPaid (attendance1, attendance2) {
  const first = toCode(attendance1)
  if (first === ConstList.PAID) return true
  return first !== null &&
    AFTERNOON_DECIDED_BY_SECOND.indexOf(first) >= 0 &&
    toCode(attendance2) === ConstList.AFTERNOON_PAID
}

/**
 * その月の有休の消化日数（0.5 刻み）。
 *
 * @param {Array} attendance1 日ごとの勤怠1（1日目から順に）
 * @param {Array} attendance2 日ごとの勤怠2（同じ並び）
 * @returns {number} 有給の枠の数 × 0.5
 */
export function countPaidLeaveDays (attendance1, attendance2) {
  const firsts = Array.isArray(attendance1) ? attendance1 : []
  const seconds = Array.isArray(attendance2) ? attendance2 : []
  let halfDays = 0
  firsts.forEach((first, i) => {
    if (morningIsPaid(first)) halfDays++
    if (afternoonIsPaid(first, seconds[i])) halfDays++
  })
  return halfDays * 0.5
}
